import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Usb, Warning, Person, PhoneAndroid } from "@material-ui/icons";
import CircularProgress from "@material-ui/core/CircularProgress";

const VERSION_URLS = {
  salon: "https://app.vatansms.net/salonrandevu-version.json",
  en: "https://app.vatansms.net/enrandevu-version.json",
};

const ESignatureRequiredOverlay = ({
  show,
  minHeight = 620,
  topOffset = 0,
  certificateExpired = false,
  certificateExpiredDate = null,
  nationalIdMissing = false,
  tcMismatch = false,
  compact = false,
  inline = false,
  onMobileSignIn,
  mobileLoading = false,
  mobileError = null,
}) => {
  const [versionData, setVersionData] = useState(null);

  useEffect(() => {
    if (!show) return;
    const url = VERSION_URLS[process.env.APP_NAME] || VERSION_URLS.salon;
    fetch(url)
      .then((res) => res.json())
      .then((data) => setVersionData(data))
      .catch(() => {});
  }, [show]);

  if (!show) return null;

  const iconSize = compact ? 36 : 48;
  const cardPadding = compact ? "24px 28px" : "40px 48px";
  const stepsPadding = compact ? "16px 20px" : "24px 28px";
  const titleSize = compact ? "20px" : "24px";
  const btnPadding = compact ? "10px 20px" : "14px 32px";
  const btnFontSize = compact ? "13px" : "15px";
  const btnMinWidth = compact ? "150px" : "180px";
  const stepMargin = compact ? "10px" : "14px";

  const WrapperComponent = inline ? InlineWrapper : Overlay;
  const wrapperStyle = inline ? {} : { minHeight: `${minHeight}px`, top: `${topOffset}px` };

  if (nationalIdMissing) {
    return (
      <WrapperComponent style={wrapperStyle}>
        <DialogCard style={{ padding: cardPadding, ...(inline ? { boxShadow: "none", width: "100%", maxWidth: "100%" } : {}) }}>
          <IconContainer style={{ marginBottom: compact ? 8 : 16 }}>
            <Person style={{ fontSize: iconSize, color: "#ff9800" }} />
          </IconContainer>
          <Title style={{ fontSize: titleSize, color: "#ff9800" }}>Kimlik Numarası Gerekli</Title>
          <Subtitle style={{ marginBottom: compact ? 16 : 28 }}>
          E-İmzanın aktif olabilmesi için giriş yapmış olduğunuz hesabın TC kimlik numarası girili ve E-İmza cihazı ile uyuşması lazım
          </Subtitle>

          <StepsContainer style={{ padding: stepsPadding, marginBottom: compact ? 16 : 24 }}>
            <StepsTitle style={{ marginBottom: compact ? 12 : 18 }}>NE YAPMALISINIZ?</StepsTitle>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#ff9800">1</StepNumber>
              <StepText>Profilim sayfasına gidin.</StepText>
            </Step>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#2196f3">2</StepNumber>
              <StepText>"Kimlik No (TC)" alanına T.C. kimlik numaranızı girin.</StepText>
            </Step>
            <Step>
              <StepNumber bgColor="#4caf50">3</StepNumber>
              <StepText>Bilgilerinizi kaydedip bu sayfaya geri dönün.</StepText>
            </Step>
          </StepsContainer>

          <ProfileButton
            href="/profile"
            style={{ padding: btnPadding, fontSize: btnFontSize, minWidth: btnMinWidth }}
          >
            <Person style={{ fontSize: 20 }} />
            Profilime Git
          </ProfileButton>
        </DialogCard>
      </WrapperComponent>
    );
  }

  if (tcMismatch) {
    return (
      <WrapperComponent style={wrapperStyle}>
        <DialogCard style={{ padding: cardPadding, ...(inline ? { boxShadow: "none", width: "100%", maxWidth: "100%" } : {}) }}>
          <IconContainer style={{ marginBottom: compact ? 8 : 16 }}>
            <Warning style={{ fontSize: iconSize, color: "#f44336" }} />
          </IconContainer>
          <Title style={{ fontSize: titleSize, color: "#f44336" }}>Kimlik Numarası Uyuşmuyor</Title>
          <Subtitle style={{ marginBottom: compact ? 16 : 28 }}>
            E-imza cihazındaki T.C. kimlik numarası, sisteme kayıtlı kimlik numaranız ile eşleşmiyor.
          </Subtitle>

          <StepsContainer style={{ padding: stepsPadding, marginBottom: compact ? 16 : 24 }}>
            <StepsTitle style={{ marginBottom: compact ? 12 : 18 }}>NE YAPMALISINIZ?</StepsTitle>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#f44336">1</StepNumber>
              <StepText>Profil sayfanızdan T.C. kimlik numaranızı kontrol edin veya ekleyin.</StepText>
            </Step>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#2196f3">2</StepNumber>
              <StepText>E-imza cihazınızın size ait olduğundan emin olun.</StepText>
            </Step>
            <Step>
              <StepNumber bgColor="#ff9800">3</StepNumber>
              <StepText>Kimlik numaranız doğruysa yöneticiniz ile iletişime geçin.</StepText>
            </Step>
          </StepsContainer>
        </DialogCard>
      </WrapperComponent>
    );
  }

  if (certificateExpired) {
    return (
      <WrapperComponent style={wrapperStyle}>
        <DialogCard style={{ padding: cardPadding, ...(inline ? { boxShadow: "none", width: "100%", maxWidth: "100%" } : {}) }}>
          <IconContainer>
            <Warning style={{ fontSize: iconSize, color: "#f44336" }} />
          </IconContainer>
          <Title style={{ color: "#f44336", fontSize: titleSize }}>Sertifika Süresi Dolmuş</Title>
          <Subtitle>
            E-imza sertifikanızın geçerlilik süresi dolmuştur.
            {certificateExpiredDate && (
              <span style={{ display: "block", marginTop: 8, fontWeight: 600, color: "#f44336" }}>
                Son geçerlilik tarihi: {certificateExpiredDate}
              </span>
            )}
          </Subtitle>

          <StepsContainer style={{ padding: stepsPadding }}>
            <StepsTitle>NE YAPMALISINIZ?</StepsTitle>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#f44336">1</StepNumber>
              <StepText>E-imza sertifikanızı yenilemek için sertifika sağlayıcınıza başvurun.</StepText>
            </Step>
            <Step style={{ marginBottom: stepMargin }}>
              <StepNumber bgColor="#2196f3">2</StepNumber>
              <StepText>Yeni sertifikanızı aldıktan sonra masaüstü uygulamasını yeniden başlatın.</StepText>
            </Step>
            <Step>
              <StepNumber bgColor="#ff9800">3</StepNumber>
              <StepText>E-imza cihazınızı takarak PIN'inizi tekrar girin.</StepText>
            </Step>
          </StepsContainer>
        </DialogCard>
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent style={wrapperStyle}>
      <DialogCard style={{ padding: cardPadding, ...(inline ? { boxShadow: "none", width: "100%", maxWidth: "100%" } : {}) }}>
        <IconContainer style={{ marginBottom: compact ? 8 : 16 }}>
          <Usb style={{ fontSize: iconSize, color: "#f57c00" }} />
        </IconContainer>
        <Title style={{ fontSize: titleSize }}>E-İmza Gerekli</Title>
        <Subtitle style={{ marginBottom: compact ? 16 : 28 }}>Masaüstü e-İmza uygulamasını bağlayın.</Subtitle>

        <StepsContainer style={{ padding: stepsPadding, marginBottom: compact ? 16 : 24 }}>
          <StepsTitle style={{ marginBottom: compact ? 12 : 18 }}>NASIL KULLANILIR?</StepsTitle>

          <Step style={{ marginBottom: stepMargin }}>
            <StepNumber bgColor="#ff9800">1</StepNumber>
            <StepText>{process.env.APP_NAME === "salon" ? "Salon Randevu e-imza uygulamasını" : process.env.APP_NAME === "en" ? "En Randevu e-imza uygulamasını" : ""} indirip çalıştırın.</StepText>
          </Step>

          <Step style={{ marginBottom: stepMargin }}>
            <StepNumber bgColor="#ff9800">2</StepNumber>
            <StepText>Kullanıcı adı ve parolanız ile girin.</StepText>
          </Step>

          <Step style={{ marginBottom: stepMargin }}>
            <StepNumber bgColor="#ff9800">3</StepNumber>
            <StepText>E-imza cihazınızı bilgisayara takın.</StepText>
          </Step>

          <Step style={{ marginBottom: stepMargin }}>
            <StepNumber bgColor="#ff9800">4</StepNumber>
            <StepText>
              E-imza uygulamasında E-İmza PIN'inizi girin.
            </StepText>
          </Step>

          <Step>
            <StepCheck>✓</StepCheck>
            <StepText style={{ color: "#4caf50", fontWeight: 500 }}>
              Bu işlemlerin ardından SMS gönderme alanları kullanımınıza
              açılacaktır.
            </StepText>
          </Step>
        </StepsContainer>

        <DownloadText style={{ marginBottom: compact ? 10 : 16 }}>Uygulamayı henüz indirmediyseniz:</DownloadText>
        <ButtonsContainer>
          {versionData?.windows_url && (
            <DownloadButton
              href={versionData.windows_url}
              target="_blank"
              rel="noopener noreferrer"
              bgColor="#1976d2"
              style={{ padding: btnPadding, fontSize: btnFontSize, minWidth: btnMinWidth }}
            >
              <WindowsIcon viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M3,12V6.75L9,5.43V11.91L3,12M20,3V11.75L10,11.97V5.21L20,3M3,13L9,13.09V19.9L3,18.75V13M10,13.2L20,13.25V22L10,20.09V13.2Z"
                />
              </WindowsIcon>
              Windows Sürümü
            </DownloadButton>
          )}
          {versionData?.mac_url && (
            <DownloadButton
              href={versionData.mac_url}
              target="_blank"
              rel="noopener noreferrer"
              bgColor="#1a1a1a"
              style={{ padding: btnPadding, fontSize: btnFontSize, minWidth: btnMinWidth }}
            >
              <AppleIcon viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,16.56 2.93,11.3 4.7,7.86C5.57,6.16 7.36,5.07 9.28,5.05C10.59,5.03 11.83,5.92 12.63,5.92C13.44,5.92 14.95,4.84 16.56,5.02C17.23,5.05 19.16,5.29 20.38,7.08C20.27,7.15 18.22,8.35 18.25,10.83C18.28,13.79 20.89,14.82 20.92,14.83C20.89,14.91 20.48,16.38 19.41,17.88L18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"
                />
              </AppleIcon>
              macOS Sürümü
            </DownloadButton>
          )}
        </ButtonsContainer>

        {onMobileSignIn && (
          <>
            <Divider />
            <DownloadText style={{ marginBottom: compact ? 8 : 12 }}>
              veya mobil e-imza ile giriş yapın:
            </DownloadText>
            {mobileLoading ? (
              <MobilePendingContainer>
                <CircularProgress size={28} style={{ color: "#4caf50" }} />
                <MobilePendingText>
                  Mobil onay bekleniyor...<br />
                  <span>Telefonunuzdaki e-imza uygulamasından onaylayın.</span>
                </MobilePendingText>
              </MobilePendingContainer>
            ) : (
              <MobileButton
                onClick={onMobileSignIn}
                style={{ padding: btnPadding, fontSize: btnFontSize, minWidth: btnMinWidth }}
              >
                <PhoneAndroid style={{ fontSize: 20 }} />
                Mobil ile Giriş Yap
              </MobileButton>
            )}
            {mobileError && (
              <MobileErrorText>{mobileError}</MobileErrorText>
            )}
          </>
        )}
      </DialogCard>
    </WrapperComponent>
  );
};

export default ESignatureRequiredOverlay;

const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
`;

const DialogCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px 48px;
  max-width: 520px;
  width: 90%;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const IconContainer = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0 0 28px 0;
`;

const StepsContainer = styled.div`
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px 28px;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  margin-bottom: 24px;
`;

const StepsTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #999;
  margin: 0 0 18px 0;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepNumber = styled.span`
  min-width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${(props) => props.bgColor || "#ccc"};
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepCheck = styled.span`
  min-width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #4caf50;
  color: white;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepText = styled.span`
  font-size: 14px;
  color: #444;
  line-height: 1.4;
`;

const DownloadText = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0 0 16px 0;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 32px;
  border-radius: 10px;
  background: ${(props) => props.bgColor || "#333"};
  color: white;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  min-width: 180px;

  &:hover {
    opacity: 0.9;
  }
`;

const WindowsIcon = styled.svg`
  width: 22px;
  height: 22px;
`;

const AppleIcon = styled.svg`
  width: 22px;
  height: 22px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #e0e0e0;
  margin: 20px 0;
`;

const MobileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 32px;
  border-radius: 10px;
  background: #4caf50;
  color: white;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  min-width: 180px;
  border: none;
  outline: none;

  &:hover {
    opacity: 0.9;
  }
`;

const MobilePendingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 24px;
  background: #f1f8e9;
  border-radius: 12px;
  border: 1.5px solid #c5e1a5;
  width: 100%;
  box-sizing: border-box;
`;

const MobilePendingText = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #33691e;
  margin: 0;
  text-align: left;
  line-height: 1.4;

  span {
    font-weight: 400;
    font-size: 13px;
    color: #558b2f;
  }
`;

const MobileErrorText = styled.p`
  font-size: 13px;
  color: #d32f2f;
  margin: 10px 0 0 0;
  font-weight: 500;
`;

const ProfileButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 32px;
  border-radius: 10px;
  background: #ff9800;
  color: white;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  min-width: 180px;

  &:hover {
    opacity: 0.9;
  }
`;
